import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  VueAuthenticationComponent,
  VueAuthorityChecker, VueExternalApplicationComponent,
  VueExternalApplicationLogoutComponent,
  VueFirstTimeLoginComponent,
  VueLogoutComponent,
  VueNotAuthorizedComponent,
  VuePageNotFoundComponent,
  VuePasswordExpiredComponent,
  VuePleaseSelectComponent
} from '@vue/vueframework';
import { AccountMaintenanceComponent } from './account-maintenance/account-maintenance.component';
import { AffiliatesComponent } from './affiliates/affiliates.component';
import { AppComponent } from './app.component';
import { ChangePasswordRecoveryComponent } from './change-password-recovery/change-password-recovery.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { DownloadResourceFileComponent } from './download-resource-file/download-resource-file.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { LiteShellComponent } from './lite-shell/lite-shell.component';
import { PersonalInformationComponent } from './personal-information/personal-information.component';
import { SecurityQuestionsComponent } from './security-questions/security-questions.component';
import { AffiliateManagementAccessChecker } from './shared/guards/affiliate-management-access-checker';
import { SiteAccessChecker } from './shared/guards/site-access-checker';
import { VerifyAuthWithRedirect } from './shared/guards/verify-auth-with-redirect';
import { PersonalInformationResolve } from './shared/resolvers/personal-information.resolve';
import { SecurityQuestionsResolve } from './shared/resolvers/security-questions.resolve';
import { SelectedQuestionsResolve } from './shared/resolvers/selected-questions.resolve';
import { SiteAffiliatesResolve } from './shared/resolvers/site-affiliates.resolve';
import { VueIdCheckResolver } from './shared/resolvers/vue-id-check.resolver';
import { VueIdResolver } from './shared/resolvers/vue-id.resolver';
import { SiteAffiliateNotAuthorizedComponent } from './site-affiliate-not-authorized/site-affiliate-not-authorized.component';
import { TedhubBackupComponent } from './tedhub-backup/tedhub-backup.component';
import { UsernameLookupComponent } from './username-lookup/username-lookup.component';
import { VueIdValidationComponent } from './vue-id-validation/vue-id-validation.component';

/**
 * These are routes based on the base being app component.
 */
const appCompRoutes: Routes = [
  {
    path: '',
    component: AppComponent,
    children: [
      {
        path: 'authenticate',
        component: VueAuthenticationComponent
      },
      {
        path: 'externalLogout',
        component: VueLogoutComponent
      },
      {
        path: 'vue-id-check',
        component: VueIdValidationComponent,
        resolve: {
          showVueId: VueIdCheckResolver
        }
      },
      {
        path: 'account/maintenance',
        component: AccountMaintenanceComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'personalInformation'
          },
          {
            path: 'personalInformation',
            component: PersonalInformationComponent,
            resolve: {
              personalInformation: PersonalInformationResolve,
              vueId: VueIdResolver
            }
          },
          {
            path: 'changePassword',
            component: ChangePasswordComponent
          },
          {
            path: 'securityQuestions',
            component: SecurityQuestionsComponent,
            resolve: {
              securityQuestions: SecurityQuestionsResolve,
              selectedQuestions: SelectedQuestionsResolve
            }
          }
        ],
        data: { authData: { authorizedRoles: [ 'TEST_CENTER_USER' ] } },
        canActivate: [ VueAuthorityChecker ]
      },
      {
        path: 'affiliates',
        component: AffiliatesComponent,
        canActivate: [ AffiliateManagementAccessChecker ],
        resolve: {
          siteAffiliateList: SiteAffiliatesResolve
        }
      },
      {
        path: 'externalAppLauncher',
        component: VueExternalApplicationComponent,
        data: {
          authData: {
            appId: 14,
            authorizedRoles: [ 'TEST_CENTER_USER' ]
          }
        },
        canActivate: [ VueAuthorityChecker ]
      },
      {
        path: 'intelliVueAppLauncher',
        component: VueExternalApplicationComponent,
        data: {
          authData: {
            appId: 105,
            authorizedRoles: [ 'BIRPT_ALLCLIENTS_TESTCENTERS_WEB_POWER_TC_REPORTS' ]
          }
        },
        canActivate: [ SiteAccessChecker, VueAuthorityChecker ]
      },
      {
        path: 'externalAppLogout',
        component: VueExternalApplicationLogoutComponent,
        data: {
          translationBundle: 'PortalBase'
        }
      },
      {
        path: 'passwordexpired',
        component: VuePasswordExpiredComponent
      },
      {
        path: 'passwordReset',
        component: ForgotPasswordComponent
      },
      {
        path: 'firsttimelogin/:userId/:hash',
        component: VueFirstTimeLoginComponent
      },
      {
        path: 'usernamelookup',
        component: UsernameLookupComponent
      },
      {
        path: 'security-questions/change-password',
        component: ChangePasswordRecoveryComponent
      },
      {
        path: 'changepassword/:userID/:hash',
        component: ChangePasswordRecoveryComponent
      },
      {
        path: 'pleaseSelect',
        component: VuePleaseSelectComponent,
        data: { authData: { authorizedRoles: [ '*all' ] } },
        canActivate: [ VueAuthorityChecker ]
      },
      {
        path: 'notAuthorized',
        component: VueNotAuthorizedComponent
      },
      {
        path: 'ted-authorization',
        component: TedhubBackupComponent
      },
      {
        path: 'siteAffiliateNotAuthorized',
        component: SiteAffiliateNotAuthorizedComponent
      },
      {
        path: '**',
        component: VuePageNotFoundComponent
      }
    ]
  }
];

const routes: Routes = [
  { path: '', redirectTo: '/pleaseSelect', pathMatch: 'full' },
  {
    path: 'authenticate/login',
    pathMatch: 'full',
    redirectTo: '/authenticate'
  },
  {
    path: '',
    children: [
      {
        path: 'external-file-download/:fileId/:fileName',
        component: LiteShellComponent,
        children: [ {
          path: '',
          component: DownloadResourceFileComponent
        } ],
        canActivate: [ VerifyAuthWithRedirect ]
      },
      {
        path: '',
        children: appCompRoutes
      }
    ]
  }
];

@NgModule({
  imports: [ RouterModule.forRoot(routes, {
    enableTracing: false,
    useHash: true
  }) ],
  exports: [ RouterModule ]
})
export class AppRoutingModule { }
